import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ClipboardList, Download, Loader2 } from "lucide-react";
import { StagingSecurityScanPanel } from "@/components/admin/StagingSecurityScanPanel";
import { SecurityAuditExportPanel } from "@/components/admin/SecurityAuditExportPanel";
import { BlueprintPdfProtectionPanel } from "@/components/admin/BlueprintPdfProtectionPanel";

type Finding = {
  id: string;
  internal_id: string;
  scanner_name: string;
  level: string | null;
  name: string | null;
  description: string | null;
  status: "open" | "ignored" | "fixed";
  ignore_reason: string | null;
  decided_by: string | null;
  decided_at: string | null;
  last_seen_at: string;
  created_at: string;
};

const STATUSES = ["all", "open", "ignored", "fixed"] as const;

const csvCell = (v: unknown) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function AdminSecurityRegister() {
  const [findings, setFindings] = useState<Finding[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("security_scan_findings")
        .select(
          "id, internal_id, scanner_name, level, name, description, status, ignore_reason, decided_by, decided_at, last_seen_at, created_at",
        )
        .order("last_seen_at", { ascending: false })
        .limit(1000);
      setFindings((data as Finding[]) ?? []);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return findings.filter((f) => {
      if (status !== "all" && f.status !== status) return false;
      if (!q) return true;
      return (
        f.internal_id.toLowerCase().includes(q) ||
        (f.name ?? "").toLowerCase().includes(q) ||
        (f.ignore_reason ?? "").toLowerCase().includes(q)
      );
    });
  }, [findings, status, query]);

  const counts = useMemo(() => {
    const c = { open: 0, ignored: 0, fixed: 0 };
    for (const f of findings) c[f.status] = (c[f.status] ?? 0) + 1;
    return c;
  }, [findings]);

  const exportCsv = () => {
    const headers = [
      "internal_id",
      "scanner_name",
      "level",
      "name",
      "status",
      "dismissal_reasoning",
      "decided_at",
      "last_seen_at",
    ];
    const csv = [
      headers.join(","),
      ...filtered.map((f) =>
        [
          f.internal_id,
          f.scanner_name,
          f.level ?? "",
          f.name ?? "",
          f.status,
          f.status === "ignored" ? f.ignore_reason ?? "(no reasoning recorded)" : "",
          f.decided_at ?? "",
          f.last_seen_at,
        ]
          .map(csvCell)
          .join(","),
      ),
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `security-findings-register-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container mx-auto max-w-6xl px-4 py-8 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <ClipboardList className="size-6 text-primary" />
            Security findings register
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every scan result with its internal id, current status, and the exact reasoning recorded
            for anything dismissed as non-actionable.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv}>
            <Download className="size-4 mr-2" />
            Export register CSV
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/security-findings">
              <ChevronLeft className="size-4 mr-2" />
              Findings workspace
            </Link>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-40">
          <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <SelectTrigger className="h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s === "ignored" ? "dismissed" : s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search internal_id, name or reasoning…"
          className="h-9 max-w-sm text-xs"
        />
        <span className="font-mono text-[10px] uppercase text-muted-foreground">
          {counts.open} open · {counts.ignored} dismissed · {counts.fixed} fixed
        </span>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        {loading ? (
          <p className="p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" /> Loading register…
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-muted-foreground font-mono text-[10px] uppercase">
              <tr className="border-b border-border">
                <th className="text-left p-3">internal_id</th>
                <th className="text-left p-3">Scanner</th>
                <th className="text-left p-3">Level</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Reasoning (if dismissed)</th>
                <th className="text-left p-3">Decided</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-muted-foreground">
                    No findings match these filters.
                  </td>
                </tr>
              )}
              {filtered.map((f) => (
                <tr key={f.id} className="border-b border-border/50 align-top">
                  <td className="p-3 font-mono text-[11px]">
                    {f.internal_id}
                    {f.name && (
                      <span className="block text-muted-foreground font-sans">{f.name}</span>
                    )}
                  </td>
                  <td className="p-3 font-mono text-[10px]">{f.scanner_name}</td>
                  <td className="p-3 font-mono text-[10px]">{f.level ?? "—"}</td>
                  <td
                    className={`p-3 font-mono text-[10px] uppercase ${
                      f.status === "open"
                        ? "text-destructive"
                        : f.status === "fixed"
                          ? "text-primary"
                          : "text-muted-foreground"
                    }`}
                  >
                    {f.status === "ignored" ? "dismissed" : f.status}
                  </td>
                  <td className="p-3 max-w-md">
                    {f.status === "ignored" ? (
                      f.ignore_reason ? (
                        <span>{f.ignore_reason}</span>
                      ) : (
                        <span className="text-destructive">No reasoning recorded</span>
                      )
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-3 whitespace-nowrap text-muted-foreground">
                    {f.decided_at ? new Date(f.decided_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <StagingSecurityScanPanel />
      <SecurityAuditExportPanel />
      <BlueprintPdfProtectionPanel />
    </div>
  );
}
